import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import fvLogo from "../../../../Assets/image/fvLogo.svg";
import QuotationTable from "./QuotationTable";
import Icons from "../../../../Assets/Icons";
import ToastPopup from "../../../../widgets/Toast/toastPopup.js";
import { uploadMedia } from "../../../../modules/patientProfile/patientProfileThunk";
import { extractUploadedFileUrl, getFileNameFromUrl } from "../../../../utils/fileHelpers";
import { getLocalizedTitle } from "../../../../utils/localizedText";
import { openSecureFileUrl } from "../../../../utils/secureFilePreview";
import { calculateQuotationTotal } from "../../../../utils/quotationPricing";
import { fetchTransportationFee } from "../../../../modules/NursignCare/TransportationFee/transportationFeeThunk";
import "./QuotationDetails.css";

// Prefills from the matching persisted service (by index — visits/services are 1:1 index-aligned
// throughout this flow) when the backend already has per_time/serviceFee/transportFee/additionalFee
// for it, instead of leaving the fields blank.
const buildItemsFromVisits = (visits, distanceInKm, persistedServices = []) => {
  return (visits || []).map((v, index) => {
    const persisted = persistedServices[index];
    const price = v.serviceItemData?.price;
    const catalogFee = parseFloat(price?.wh?.opdInternationalPrice || price?.wh?.opdLocalPrice || 0);
    const dateRange = v.isMultipleDay ? `${v.startDate} - ${v.endDate}` : v.singleDate || "";
    const serviceItemTitle = v.serviceItemData?.title || v.serviceItemTitle || v.serviceItemName;

    return {
      serviceName: getLocalizedTitle(serviceItemTitle) || "-",
      serviceItemTitle,
      serviceItemData: v.serviceItemData || null,
      dateRange,
      startTime: v.startTime || "",
      endTime: v.endTime || "",
      distanceInKm,
      unitFee: persisted?.serviceFee ?? catalogFee,
      perTime: persisted?.per_time ?? 1,
      transportationFee: persisted?.transportFee ?? "",
      adjustment: persisted?.additionalFee ?? "",
    };
  });
};

const QuotationDetails = ({ setPage, visitsData, distanceInKm, existingQuotationURL, persistedServices, quotationPdfData, onFinalSubmit }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const transportationFeeConfig = useSelector((state) => state.transportationFee?.fee);

  const [quotationItems, setQuotationItems] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [quotationURL, setQuotationURL] = useState("");
  const [isUploadingQuotation, setIsUploadingQuotation] = useState(false);

  useEffect(() => {
    if (visitsData?.length) {
      setQuotationItems(buildItemsFromVisits(visitsData, distanceInKm, persistedServices));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitsData, distanceInKm]);

  useEffect(() => {
    if (!transportationFeeConfig) dispatch(fetchTransportationFee());
  }, [dispatch, transportationFeeConfig]);

  // Hydrate from a previously uploaded quotation so it can be viewed/replaced instead of looking empty
  useEffect(() => {
    if (existingQuotationURL && !pdfFile) {
      setQuotationURL(existingQuotationURL);
    }
  }, [existingQuotationURL]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUnitFeeChange = (index, value) => {
    setQuotationItems((prev) => prev.map((item, i) => (i === index ? { ...item, unitFee: value } : item)));
  };

  const handlePerTimeChange = (index, value) => {
    setQuotationItems((prev) => prev.map((item, i) => (i === index ? { ...item, perTime: value } : item)));
  };

  const handleTransportationFeeChange = (index, value) => {
    setQuotationItems((prev) => prev.map((item, i) => (i === index ? { ...item, transportationFee: value } : item)));
  };

  const handleAdjustmentChange = (index, value) => {
    setQuotationItems((prev) => prev.map((item, i) => (i === index ? { ...item, adjustment: value } : item)));
  };

  useEffect(() => {
    return () => {
      if (pdfPreview) URL.revokeObjectURL(pdfPreview);
    };
  }, [pdfPreview]);

  const setSelectedQuotationFile = async (file) => {
    if (!file) return;
    if (file.type !== "application/pdf") {
      ToastPopup.error(t("pleaseUploadPdfOnly"));
      return;
    }

    if (pdfPreview) URL.revokeObjectURL(pdfPreview);
    const localPreviewUrl = URL.createObjectURL(file);
    setPdfFile(file);
    setPdfPreview(localPreviewUrl);
    setQuotationURL("");
    setIsUploadingQuotation(true);

    try {
      const result = await dispatch(uploadMedia({ files: [file] }));
      if (result.meta.requestStatus !== "fulfilled") {
        throw new Error(result.error?.message || "Upload failed");
      }

      const uploadedUrl = extractUploadedFileUrl(result.payload);
      if (!uploadedUrl) {
        throw new Error("Upload completed without a file URL");
      }

      setQuotationURL(uploadedUrl);
    } catch (error) {
      URL.revokeObjectURL(localPreviewUrl);
      setPdfFile(null);
      setPdfPreview(null);
      setQuotationURL("");
      ToastPopup.error(error.message || t("bulkUploadFailedGeneric") || "Upload failed");
    } finally {
      setIsUploadingQuotation(false);
    }
  };

  const handleFileUpload = (e) => {
    setSelectedQuotationFile(e.target.files[0]);
    e.target.value = null;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setSelectedQuotationFile(e.dataTransfer.files[0]);
  };

  const handleRemovePdf = () => {
    if (pdfPreview) URL.revokeObjectURL(pdfPreview);
    setPdfFile(null);
    setPdfPreview(null);
    setQuotationURL("");
  };

  const handleSendQuote = () => {
    if (isUploadingQuotation) {
      ToastPopup.error(t("loading") || "Please wait for upload to finish");
      return;
    }
    const uploadedQuotationURL = quotationURL.trim() || null;
    // Total per service = (serviceFee × per_time) + transportFee + additionalFee, summed across services
    const total = calculateQuotationTotal(
      quotationItems.map((item) => ({ serviceFee: item.unitFee, transportFee: item.transportationFee, additionalFee: item.adjustment, perTime: item.perTime })),
    );
    const localizedQuotationItems = quotationItems.map((item) => ({
      ...item,
      serviceName: getLocalizedTitle(item.serviceItemData || item.serviceItemTitle, i18n.language) || item.serviceName,
      // Canonical field names expected by the backend's itemized services[] payload
      per_time: item.perTime,
      serviceFee: item.unitFee,
      transportFee: item.transportationFee,
      additionalFee: item.adjustment,
    }));
    onFinalSubmit?.({ quotationURL: uploadedQuotationURL, price: String(total), quotationItems: localizedQuotationItems });
    ToastPopup.success(t("quoteSent"));
  };

  const handleDownloadQuotation = async () => {
    const patient = quotationPdfData?.patient || {};
    const request = quotationPdfData?.request || {};
    const asPricingItem = (item) => ({ serviceFee: item.unitFee, transportFee: item.transportationFee, additionalFee: item.adjustment, perTime: item.perTime });
    const grandTotal = calculateQuotationTotal(quotationItems.map(asPricingItem));
    const pdf = new jsPDF("p", "mm", "a4");
    const isVietnamese = (i18n.language?.toLowerCase() || "en").startsWith("vi") || (i18n.language?.toLowerCase() || "en").startsWith("vn");
    const formatVnd = (amount) => Math.round(Number(amount) || 0).toLocaleString("en-US");
    const text = (value, fallback = "-") => {
      if (Array.isArray(value)) return value.map((line) => text(line, ""));
      return String(value || fallback).replace(/[^\x20-\x7E]/g, " ");
    };
    const loadImage = (source) => new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = source;
    });
    const addProjectLogo = async () => {
      const image = await loadImage(fvLogo);
      const canvas = document.createElement("canvas");
      canvas.width = 548;
      canvas.height = 100;
      canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
      pdf.addImage(canvas.toDataURL("image/png"), "PNG", 9, 9, 45, 8, undefined, "FAST");
    };
    const write = (value, x, y, options = {}) => {
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", options.bold ? "bold" : "normal");
      pdf.setFontSize(options.size || 8);
      pdf.text(text(value, ""), x, y, { align: options.align || "left", maxWidth: options.maxWidth });
    };
    const lineText = (value, width, size = 7) => {
      pdf.setFontSize(size);
      return pdf.splitTextToSize(text(value, ""), width);
    };
    const now = new Date();
    const date = now.toLocaleDateString(isVietnamese ? "vi-VN" : "en-GB");
    const time = now.toLocaleTimeString(isVietnamese ? "vi-VN" : "en-US", { hour: "2-digit", minute: "2-digit" });
    const language = isVietnamese ? "vi" : "en";

    // Shared Patient Information container used on both Page 1 and Page 2
    const renderPatientInformation = (startY = 28) => {
      pdf.setFillColor(184, 222, 206);
      pdf.rect(12, startY, 186, 6, "F");
      write(isVietnamese ? "THONG TIN BENH NHAN" : "PATIENT INFORMATION", 14, startY + 4.2, { size: 8, bold: true });
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.25);
      pdf.rect(12, startY, 186, 38);
      pdf.setDrawColor(190, 190, 190);
      pdf.line(105, startY + 6, 105, startY + 38);
      write(isVietnamese ? "Ho va ten benh nhan" : "Patient full name", 15, startY + 14, { size: 6.5 });
      write(patient.name, 15, startY + 19, { size: 8, bold: true, maxWidth: 82 });
      write(isVietnamese ? "So dien thoai" : "Phone number", 15, startY + 27, { size: 6.5 });
      write(patient.phone, 15, startY + 32, { size: 8, maxWidth: 82 });
      write(isVietnamese ? "Ma benh nhan" : "Hospital number", 109, startY + 14, { size: 6.5 });
      write(patient.hnNumber, 109, startY + 19, { size: 8, bold: true, maxWidth: 37 });
      write(isVietnamese ? "Ngay va gio bao gia" : "Quotation date & time", 151, startY + 14, { size: 6.5 });
      write(`${date} ${time}`, 195, startY + 19, { size: 7.5, bold: true, align: "right" });
      write(isVietnamese ? "Dia chi dich vu" : "Service address", 109, startY + 27, { size: 6.5 });
      write(lineText(patient.address, 84, 7), 109, startY + 32, { size: 7, maxWidth: 84 });
    };

    // --- PAGE 1 ---
    await addProjectLogo();
    pdf.setTextColor(0, 101, 60);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text(isVietnamese ? "BAO GIA DICH VU DU KIEN" : "ESTIMATED SERVICE QUOTATION", 112, 15, { align: "center" });
    pdf.setFontSize(7.5);
    pdf.text(isVietnamese ? "TRUNG TAM CHAM SOC SUC KHOE TAI NHA & KHAM BENH TU XA FV" : "FV HEALTHCARE AT HOME & TELEMEDICINE CENTRE", 112, 20, { align: "center" });

    renderPatientInformation(28);

    pdf.setFillColor(245, 249, 247);
    pdf.rect(12, 72, 186, 45, "F");
    pdf.setDrawColor(184, 222, 206);
    pdf.rect(12, 72, 186, 45);
    write("CO SO TINH PHI / FEE PARAMETERS", 15, 78, { size: 8, bold: true });
    write("Cau truc chi phi dich vu tai nha bao gom phi dich vu y te co ban va phi di chuyen. Phi di chuyen duoc tinh theo khoang cach thuc te tu FV Hospital den dia diem cung cap dich vu.", 15, 84, { size: 6.4, maxWidth: 178 });
    write("Our homecare service fees comprise a base medical service fee and a transportation charge. The transportation fee is calculated from FV Hospital to the requested location.", 15, 91, { size: 6.4, maxWidth: 178 });
    write("Quang duong uoc tinh / Estimated distance", 15, 99, { size: 7, bold: true });
    write(`${text(request.distanceInKm)} km`, 77, 99, { size: 7.5, bold: true });
    const firstKm = transportationFeeConfig?.firstKm;
    const firstKmText = firstKm ? `${formatVnd(firstKm)} km` : "-";
    write("BIEU PHI DI CHUYEN / TRANSPORTATION FEE STRUCTURE", 15, 106, { size: 7.2, bold: true });
    write("Khoang cach tinh cuoc ban dau / Initial billable distance", 15, 112, { size: 6.5 });
    write(firstKmText, 105, 112, { size: 7.2, bold: true });
    write("Phi di chuyen tren tung dich vu duoc lay tu cau hinh Transportation Fee hien tai va duoc hien thi trong bang bao gia ben duoi.", 15, 116, { size: 6.2, maxWidth: 178 });

    const columns = [10, 43, 18, 17, 22, 27, 24, 25];
    const xs = [12];
    columns.forEach((width) => xs.push(xs[xs.length - 1] + width));
    const tableTop = 124;
    const headings = isVietnamese
      ? ["STT", "Ten dich vu", "Moi lan", "KM", "Phi dich vu", "Phi di chuyen", "Dieu chinh", "Tam tinh"]
      : ["No.", "Service name", "Per time", "Distance", "Service fee", "Transportation fee", "Adjustment", "Subtotal"];
    pdf.setFillColor(184, 222, 206);
    pdf.rect(12, tableTop, 186, 10, "F");
    pdf.setDrawColor(0, 0, 0);
    columns.forEach((width, index) => pdf.rect(xs[index], tableTop, width, 10));
    headings.forEach((heading, index) => write(heading, xs[index] + columns[index] / 2, tableTop + 6, { size: 6.3, bold: true, align: "center", maxWidth: columns[index] - 2 }));
    const rows = quotationItems.map((item, index) => {
      const service = getLocalizedTitle(item.serviceItemData || item.serviceItemTitle, language) || item.serviceName;
      const quantity = Number(item.perTime) || 1;
      const fee = Number(item.unitFee) || 0;
      const transport = Number(item.transportationFee) || 0;
      const adjustment = Number(item.adjustment) || 0;
      const subtotal = fee * quantity + transport + adjustment;
      return [index + 1, service, quantity, `${text(request.distanceInKm)} km`, formatVnd(fee), formatVnd(transport), formatVnd(adjustment), formatVnd(subtotal)];
    });
    if (!rows.length) rows.push([1, isVietnamese ? "Dich vu cham soc tai nha" : "Homecare service", 1, `${text(request.distanceInKm)} km`, "0", "0", "0", "0"]);
    const usableRows = rows.slice(0, 5);
    const rowHeight = Math.min(18, 82 / usableRows.length);
    usableRows.forEach((row, rowIndex) => {
      const top = tableTop + 10 + rowIndex * rowHeight;
      columns.forEach((width, index) => pdf.rect(xs[index], top, width, rowHeight));
      row.forEach((value, index) => write(lineText(value, columns[index] - 2, 6.5), index === 1 ? xs[index] + 1.5 : xs[index] + columns[index] / 2, top + 5, { size: 6.5, align: index === 1 ? "left" : "center", maxWidth: columns[index] - 2 }));
    });
    const totalY = tableTop + 10 + usableRows.length * rowHeight;
    pdf.setFillColor(184, 222, 206);
    pdf.rect(12, totalY, 186, 9, "F");
    pdf.setDrawColor(0, 101, 60);
    pdf.rect(12, totalY, 186, 9);
    write(isVietnamese ? "TONG THANH TOAN" : "TOTAL PAYMENT", 15, totalY + 5.8, { size: 8, bold: true, align: "left" });
    write(`${formatVnd(grandTotal)} VND`, 195, totalY + 5.8, { size: 8, bold: true, align: "right" });

    const acknowledgementY = totalY + 16;
    pdf.setDrawColor(0, 0, 0);
    pdf.rect(12, acknowledgementY, 186, 27);
    pdf.setFillColor(184, 222, 206);
    pdf.rect(12, acknowledgementY, 186, 6, "F");
    write(isVietnamese ? "XAC NHAN" : "ACKNOWLEDGEMENT", 14, acknowledgementY + 4, { size: 8, bold: true });
    write(isVietnamese ? "Toi xac nhan da duoc tu van ve chi phi dich vu." : "I acknowledge that I have received financial counselling from FV Hospital.", 14, acknowledgementY + 11, { size: 7.2 });
    pdf.setFillColor(184, 222, 206);
    pdf.rect(14, acknowledgementY + 14, 82, 6, "F");
    pdf.rect(114, acknowledgementY + 14, 82, 6, "F");
    write(patient.name, 55, acknowledgementY + 18, { size: 7.5, align: "center", maxWidth: 78 });
    write("FV Healthcare at Home", 155, acknowledgementY + 18, { size: 7.5, align: "center", maxWidth: 78 });
    write(isVietnamese ? "Ten benh nhan / nguoi duoc tu van" : "Patient / person counselled", 55, acknowledgementY + 24, { size: 6.5, align: "center" });
    write(isVietnamese ? "Ten nguoi tu van" : "Consultant name", 155, acknowledgementY + 24, { size: 6.5, align: "center" });

    // --- PAGE 2 --- (Fully Vector-Based High Resolution)
    pdf.addPage();
    await addProjectLogo();
    pdf.setTextColor(0, 101, 60);
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text(isVietnamese ? "BAO GIA DICH VU DU KIEN" : "ESTIMATED SERVICE QUOTATION", 112, 15, { align: "center" });
    pdf.setFontSize(7.5);
    pdf.text(isVietnamese ? "TRUNG TAM CHAM SOC SUC KHOE TAI NHA & KHAM BENH TU XA FV" : "FV HEALTHCARE AT HOME & TELEMEDICINE CENTRE", 112, 20, { align: "center" });

    // Render identical patient information box on Page 2
    renderPatientInformation(28);

    // Important Notes Section
    pdf.setFillColor(184, 222, 206);
    pdf.rect(12, 72, 186, 6, "F");
    write(isVietnamese ? "LUU Y QUAN TRONG" : "IMPORTANT NOTES", 14, 76.2, { size: 8, bold: true });

    const notesVi = [
      "1. Dich vu cham soc y te tai nha tieu chuan bao gom 01 nhan vien y te. Truong hop yeu cau cham soc can 02 nhan vien, chi phi phat sinh (neu co) se duoc thong bao de Quy khach xac nhan truoc khi thuc hien.",
      "2. FV Hospital dieu dong nhan vien y te theo lich truc de dam bao chat luong va tinh lien tuc cua dich vu. Cac yeu cau chi dinh nhan vien cu the se duoc can nac nhung khong dam bao 100%.",
      "3. Dich vu cham soc tai nha duoc cung cap tren co so dat truoc va thanh toan truoc. Quy khach vui long dat va xac nhan dich vu truoc it nhat 48 gio so voi thoi gian du kien.",
      "Huy hoac thay doi lich: Vui long thong bao cho FV Hospital truoc it nhat 48 gio so voi thoi gian hen. Truong hop nhan vien da den noi, phi di chuyen se duoc tinh va so tien phi dich vu con lai se duoc hoan tra theo quy dinh cua benh vien.",
    ];

    const notesEn = [
      "1. Standard homecare services include one healthcare professional. If the care requirement needs two persons, any additional fee will be communicated for approval before the service.",
      "2. FV Hospital coordinates homecare staff on a rotational basis to ensure clinical quality and continuity. Requests for a specific individual or fixed team cannot be guaranteed.",
      "3. Homecare services are provided on a pre-booked and pre-paid basis. Please book and confirm at least 48 hours before the planned appointment.",
      "Cancellation or rescheduling: Please notify FV Hospital at least 48 hours before the appointment. If staff have already arrived, the travel fee will be charged and the remaining service fee will be refunded according to hospital policy.",
    ];

    const notes = isVietnamese ? notesVi : notesEn;
    let noteY = 85;
    notes.forEach((note) => {
      const noteLines = lineText(note, 184, 7.5);
      write(noteLines, 14, noteY, { size: 7.5, maxWidth: 184 });
      noteY += noteLines.length * 4.2 + 4;
    });

    noteY += 4;
    // Contact Information Section
    pdf.setFillColor(184, 222, 206);
    pdf.rect(12, noteY, 186, 6, "F");
    write(isVietnamese ? "THONG TIN LIEN HE" : "CONTACT INFORMATION", 14, noteY + 4.2, { size: 8, bold: true });

    noteY += 11;
    const contactsVi = [
      "Trong gio lam viec (Thu Hai - Thu Sau: 8:00 - 16:45; Thu Bay: 8:00 - 11:45): (+84) 9 6262 7826 hoac (+84) 28 3511 3389.",
      "Ngoai gio lam viec, ngay le, Chu Nhat: Le tan Khoa Cap cuu (+84) 28 3511 3500.",
      "Email: chamsocytetainha@fvhospital.com hoac telemedicine@fvhospital.com",
    ];

    const contactsEn = [
      "During working hours (Monday-Friday: 8:00 AM-4:45 PM; Saturday: 8:00 AM-11:45 AM): (+84) 9 6262 7826 or (+84) 28 3511 3389.",
      "Outside working hours, weekends or public holidays: Emergency Department Reception (+84) 28 3511 3500.",
      "Email: chamsocytetainha@fvhospital.com or telemedicine@fvhospital.com",
    ];

    const contacts = isVietnamese ? contactsVi : contactsEn;
    contacts.forEach((contact) => {
      const contactLines = lineText(contact, 184, 7.5);
      write(contactLines, 14, noteY, { size: 7.5, maxWidth: 184 });
      noteY += contactLines.length * 4.2 + 3;
    });

    // Signature lines
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.2);
    pdf.line(14, 255, 88, 255);
    pdf.line(120, 255, 196, 255);
    write(isVietnamese ? "Chu ky benh nhan / nguoi duoc tu van" : "Signature of patient / person counselled", 14, 260, { size: 6.5 });
    write(isVietnamese ? "Ten va chu ky nguoi tu van" : "Name and signature of consultant", 120, 260, { size: 6.5 });

    // Save PDF with patient's name (e.g., Tanmay-Kulkarni-Quotation.pdf)
    const formattedPatientName = patient.name
      ? patient.name
          .trim()
          .replace(/[^a-zA-Z0-9\s_-]/g, "")
          .replace(/\s+/g, "-")
      : "";
    const fileName = formattedPatientName ? `${formattedPatientName}-Quotation.pdf` : `quotation-${request.requestId || "details"}.pdf`;

    pdf.save(fileName);
  };

  return (
    <div className="quotation-details-container">
      {/* Quotation Table */}
      <div className="whp-container quotation-table-card">
        <div className="title-hr2 quotation-title-row">
          <div className="quotation-title-with-action">
            <p>{t("quotationDetails")}</p>
            <button type="button" className="download-quotation-btn" onClick={handleDownloadQuotation} disabled={isUploadingQuotation}>
              {t("downloadQuotation")}
            </button>
          </div>
          <hr />
        </div>
        <QuotationTable
          items={quotationItems}
          onUnitFeeChange={handleUnitFeeChange}
          onTransportationFeeChange={handleTransportationFeeChange}
          onAdjustmentChange={handleAdjustmentChange}
          onPerTimeChange={handlePerTimeChange}
        />
      </div>

      {/* Upload Quotation */}
      <div className="whp-container pdf-upload-card">
        <div className="title-hr2">
          <p>{t("uploadQuotationDocument")}</p>
          <hr />
        </div>
        <div className="pdf-upload-section">
          {!pdfFile && !quotationURL ? (
            <div className="pdf-upload-area" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
              <input type="file" accept="application/pdf" onChange={handleFileUpload} id="pdf-upload-input" className="pdf-upload-input" />
              <label htmlFor="pdf-upload-input" className="pdf-upload-label">
                <img src={Icons.upload} alt="upload" className="pdf-icon-small" />
                <span className="pdf-upload-text">{t("pleaseUploadQuotationPdf")}</span>
                <span className="pdf-upload-hint">{t("or") || "Or"}</span>
                <span className="pdf-browse-btn">{t("browseFiles") || "Browse files"}</span>
              </label>
            </div>
          ) : (
            <div className="pdf-preview-area">
              <div className="pdf-preview-info">
                <img src={Icons.upload} alt="PDF" className="pdf-icon-small" />
                <div className="pdf-preview-details">
                  <span className="pdf-file-name">{pdfFile ? pdfFile.name : getFileNameFromUrl(quotationURL) || t("quotationDetails")}</span>
                  {pdfFile && <span className="pdf-file-size">{(pdfFile.size / 1024).toFixed(2)} KB</span>}
                </div>
              </div>
              <div className="pdf-preview-actions">
                <button className="pdf-view-btn" onClick={() => openSecureFileUrl(quotationURL || pdfPreview)} disabled={isUploadingQuotation}>
                  {isUploadingQuotation ? t("loading") : t("view")}
                </button>
                <input type="file" accept="application/pdf" onChange={handleFileUpload} id="pdf-replace-input" className="pdf-upload-input" />
                <label htmlFor="pdf-replace-input" className="pdf-view-btn">
                  {t("replace") || "Replace"}
                </label>
                <button className="pdf-remove-btn" onClick={handleRemovePdf}>
                  <img src={Icons.trashIcon} alt="Remove" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="form-section-footer">
        <button className="form-section-cancel" onClick={() => setPage(3)}>
          {t("back")}
        </button>
        <button className="form-section-create send-quote-btn" onClick={handleSendQuote}>
          {t("sendQuote")}
        </button>
      </div>
    </div>
  );
};

export default QuotationDetails;
